import { datadogRum } from '@datadog/browser-rum';
import { getCookie } from 'stash/utils/cookies';
import type { SuperCookieV1 } from 'stash/utils/superCookie';

export type UtmField =
	| 'utm_source'
	| 'utm_medium'
	| 'utm_campaign'
	| 'utm_content'
	| 'utm_term'
	| 'utm_provider';

export const SUPER_COOKIE_UTM_FALLBACK_ACTION = 'super_cookie.utm_fallback_used';

/**
 * Reads a UTM value preferring the super cookie over the individual legacy
 * cookie. The super cookie is the canonical source; the individual cookie
 * fallback exists only during the transition period while we consolidate
 * attribution into `stashCookie`.
 *
 * Blank/whitespace super-cookie values are treated as missing so we don't
 * drop attribution when a super cookie payload exists but has an empty UTM
 * field.
 *
 * `getSuperCookie` uses an unsafe `as` cast on JSON.parse output, so a
 * corrupted payload could hand us a non-string truthy value (object, number,
 * array). We guard with `typeof === 'string'` so `.trim()` cannot throw mid
 * render.
 *
 * Emits a Datadog RUM action via `datadogRum.addAction(...)` exactly when the
 * super cookie missed AND the individual cookie produced a value — i.e. the
 * fallback actually kept attribution alive. This is the signal that tells us
 * when it is safe to delete the legacy-cookie fallback and complete the
 * cookie-consolidation rollout. Silent on both success (super cookie hit) and
 * total miss (neither present; user simply has no UTMs).
 *
 * Telemetry is wrapped in try/catch so a RUM failure cannot break the caller.
 * Follows the pattern in `lazy.jsx` / `fetchWithRetry.ts`.
 *
 * @param superCookie Parsed super cookie, or `null` when absent/invalid.
 * @param field UTM field name (also used as the individual cookie name).
 * @param callsite Short label identifying the calling function, included as a
 *   RUM tag so we can see where super-cookie coverage is breaking down.
 */
export const pickUtmWithFallback = (
	superCookie: SuperCookieV1 | null,
	field: UtmField,
	callsite: string
): string | undefined => {
	const raw = superCookie?.[field];
	const superValue = typeof raw === 'string' ? raw.trim() : '';
	if (superValue) {
		return superValue;
	}

	const cookieValue = getCookie(field);
	if (cookieValue) {
		try {
			datadogRum.addAction(SUPER_COOKIE_UTM_FALLBACK_ACTION, {
				field,
				callsite,
			});
		} catch (_e) {
			// Observability is never worth breaking attribution reads for.
		}
		return cookieValue;
	}

	return undefined;
};
